import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  OverlayTrigger,
  Button,
  Tooltip,
} from "react-bootstrap";
import { DEV_URL, rget, rpost } from "../../utils/requests";
import DataTable from "react-data-table-component";
import { Link, useParams } from "react-router-dom";
import Spinner from "../../components/Spinner";
import { CompanyDetailsV2 } from "./companyDetailsV2";
import Div from "../../components/Div";
import PageTitle from "../../components/PageTitle/index";
import CustomerDetailsHeader from "./Header";
import loader from "../../assets/images/datahub-loader.svg";

const COMPANY_NOT_FOUND_MESSAGE =
  "The company you are looking for was not found or is no longer available. Please return to the companies list and try again.";

const CompaniesDetailsDataNew = () => {
  const [wasFollowClicked, setWasFollowClicked] = useState(false); // just to check changes in flips
  const [companyLicenseData, setCompanyLicenseData] = useState([]);
  const [companyContactData, setCompanyContactData] = useState(null);
  const [companyAddressData, setCompanyAddressData] = useState([]);
  const [loadingCompanyDetailsData, setLoadingCompanyDetailsData] =
    useState(true);
  const [companyDetails, setCompanyDetails] = useState();
  const [companyDetailsNotFound, setCompanyDetailsNotFound] = useState(false);
  const [companyAliases, setCompanyAliases] = useState([]);
  const [domainRefreshKey, setDomainRefreshKey] = useState(0);
  let { companyId } = useParams();
  const [isSubscribe, setIsSubscribe] = useState(false);

  const fetchCompanyDetails = React.useCallback(async () => {
    setCompanyDetailsNotFound(false);

    try {
      const _companyDetails = await rget(
        `${DEV_URL}/api/company/${companyId.toUpperCase()}`
      );

      const company = _companyDetails?.data;

      if (!company || company === "Data not found") {
        setCompanyDetails(null);
        setIsSubscribe(false);
        setCompanyDetailsNotFound(true);
        return null;
      }

      setIsSubscribe(company.isSubscribe);
      setCompanyDetails(company);
      return company;
    } catch (error) {
      console.error("Error fetching company details:", error);
      setCompanyDetails(null);
      setIsSubscribe(false);
      setCompanyDetailsNotFound(true);
      return null;
    }
  }, [companyId]);

  const fetchCompanyAliases = React.useCallback(async () => {
    if (!companyId) return;
    try {
      const aliasesResponse = await rpost(`${DEV_URL}/api/Company/GetAlias`, {
        PageNumber: 1,
        PageSize: 100,
        SearchTerm: "",
        companyId: companyId,
      });
      const aliases =
        aliasesResponse?.data?.aliases ??
        aliasesResponse?.data?.Aliases ??
        aliasesResponse?.data?.data?.aliases ??
        aliasesResponse?.data?.data?.Aliases ??
        aliasesResponse?.data;
      setCompanyAliases(Array.isArray(aliases) ? aliases : []);
    } catch (error) {
      console.error("Error fetching aliases:", error);
      setCompanyAliases([]);
    }
  }, [companyId]);

  const fetchCompanyContacts = React.useCallback(async () => {
    if (!companyId) return;
    try {
      const contactsResponse = await rpost(`${DEV_URL}/api/contact/getall`, {
        PageNumber: 1,
        PageSize: 100,
        SearchTerm: "",
        companyId: companyId,
      });
      setCompanyContactData(contactsResponse?.data || { contact: [] });
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setCompanyContactData({ contact: [] });
    }
  }, [companyId]);

  const fetchCompanyLicences = React.useCallback(async () => {
    if (!companyId) return;
    try {
      const LicenseResponse = await rpost(`${DEV_URL}/api/license/getall`, {
        PageNumber: 1,
        PageSize: 100,
        SearchTerm: "",
        companyId: companyId,
      });
      setCompanyLicenseData(LicenseResponse?.data?.license || []);
    } catch (error) {
      console.error("Error fetching aliases:", error);
      setCompanyLicenseData([]);
    }
  }, [companyId]);

  const refreshCompanyDomains = React.useCallback(() => {
    setDomainRefreshKey((currentKey) => currentKey + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoadingCompanyDetailsData(true);
      setCompanyDetails(null);
      setCompanyAliases([]);
      setCompanyContactData(null);
      setCompanyLicenseData([]);

      try {
        const company = await fetchCompanyDetails();

        if (!company) return;

        await Promise.all([
          fetchCompanyAliases(),
          fetchCompanyContacts(),
          fetchCompanyLicences(),
        ]);
      } finally {
        setLoadingCompanyDetailsData(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      setCompanyLicenseData([]);
      setCompanyContactData(null);
      setCompanyAddressData([]);
      setCompanyAliases([]);
    };
  }, [
    companyId,
    fetchCompanyDetails,
    fetchCompanyAliases,
    fetchCompanyContacts,
    fetchCompanyLicences,
  ]);

  if (loadingCompanyDetailsData) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "calc(100vh - 110px)" }}
      >
        <img
          src={loader}
          height={"50px"}
          alt="Loading"
          style={{ pointerEvents: "none" }}
        />
      </div>
    );
  }

  if (companyDetailsNotFound) {
    return (
      <div className="pt-2 pt-sm-5 pb-4 pb-sm-5">
        <div className="container">
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card>
                <Card.Body className="p-4">
                  <div className="text-center">
                    <h4 className="text-uppercase text-danger mt-3">
                      Company Not Found
                    </h4>
                    <p className="text-muted mt-3">
                      {COMPANY_NOT_FOUND_MESSAGE}
                    </p>
                    <Link className="btn btn-primary mt-3" to="/companies">
                      <i className="mdi mdi-reply"></i> Return to Companies
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  return (
    <>
      <CustomerDetailsHeader
        company={companyDetails}
        aliases={companyAliases}
        contacts={companyContactData}
        license={companyLicenseData}
        domainRefreshKey={domainRefreshKey}
        onCompanyUpdate={async () => {
          setLoadingCompanyDetailsData(true);
          try {
            const company = await fetchCompanyDetails();
            if (!company) return;

            await Promise.all([
              fetchCompanyAliases(),
              fetchCompanyContacts(),
              fetchCompanyLicences(),
            ]);
          } finally {
            setLoadingCompanyDetailsData(false);
          }
        }}
      />
      <CompanyDetailsV2
        companyLicenseData={companyLicenseData}
        companyContactData={companyContactData}
        companyAddressData={companyAddressData}
        companyId={companyId}
        company={companyDetails}
        subscribed={[isSubscribe, setIsSubscribe]}
        aliases={companyAliases}
        contacts={companyContactData}
        onRefreshAliases={fetchCompanyAliases}
        onRefreshDomains={refreshCompanyDomains}
      />
    </>
  );
};

export default CompaniesDetailsDataNew;
